import { X, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { formatUsdCents } from '@/lib/formatMoney';

export interface TradeItem {
  id: string;
  title: string;
  imageUrl?: string | null;
  estimatedValueCents?: number | null;
  isFromInventory: boolean;
  inventoryItemId?: string;
  // Fields for creating inventory from received items
  addToInventory?: boolean;
  category?: string;
  condition?: string;
  game?: string;
}

interface TradeItemCardProps {
  item: TradeItem;
  onRemove?: () => void;
  isReadOnly?: boolean;
  variant?: 'received' | 'gave';
}

export function TradeItemCard({ 
  item, 
  onRemove, 
  isReadOnly = false,
  variant = 'received',
}: TradeItemCardProps) {
  const valueDisplay = item.estimatedValueCents 
    ? `${formatUsdCents(item.estimatedValueCents)}`
    : null;

  return (
    <div 
      className={cn(
        "flex items-center gap-3 p-2 rounded-lg border",
        variant === 'received' 
          ? "bg-black/20 border-success/20" 
          : "bg-black/20 border-white/[0.08]"
      )}
    >
      {/* Thumbnail */}
      {item.imageUrl ? (
        <img
          src={item.imageUrl}
          alt={item.title}
          width={40}
          height={40}
          loading="lazy"
          decoding="async"
          className="w-10 h-10 rounded object-cover shrink-0"

        />
      ) : (
        <div className="w-10 h-10 rounded bg-white/[0.05] flex items-center justify-center shrink-0">
          <span className="text-xs text-muted-foreground">N/A</span>
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{item.title}</p>
        <div className="flex items-center gap-1.5">
          {!item.isFromInventory && (
            <span className="text-xs text-muted-foreground">External</span>
          )}
          {/* Show indicator when item will be added to inventory */}
          {!item.isFromInventory && item.addToInventory && (
            <span className="inline-flex items-center gap-0.5 text-xs text-primary">
              <Package className="w-3 h-3" />
              → Inventory
            </span>
          )}
        </div>
      </div>

      {/* Value */}
      {valueDisplay && (
        <span className={cn(
          "text-sm font-medium shrink-0",
          variant === 'received' ? "text-success" : "text-muted-foreground"
        )}>
          {valueDisplay}
        </span>
      )}

      {/* Remove button */}
      {!isReadOnly && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Remove trade item"
          className="w-6 h-6 shrink-0 text-muted-foreground hover:text-destructive"
          onClick={onRemove}
        >
          <X className="w-3.5 h-3.5" />

        </Button>
      )}
    </div>
  );
}